import { CarManager, HouseManager, PheromoneGrid, TerrainGrid } from '@antification/core';
import { ParkingtonTool, COLONY_PORTAL_COLORS, type ParkingtonState } from '../ParkingtonTypes';
import { Camera } from './Camera';

/**
 * Context needed for map feature rendering.
 */
export interface MapFeatureRenderContext {
  camera: Camera;
  grid: TerrainGrid;
  cars: CarManager;
  houses: HouseManager;
  pheromones: PheromoneGrid;
  state: ParkingtonState;
}

/**
 * Renders map features: pheromones, car paths, portals, location markers.
 */
export class MapFeatureRenderer {
  /**
   * Render pheromone trails visualization (debug/exploration feature).
   */
  renderPheromones(ctx: CanvasRenderingContext2D, rctx: MapFeatureRenderContext): void {
    const scale = rctx.camera.getScale();
    const cols = rctx.pheromones.getCols();
    const rows = rctx.pheromones.getRows();

    ctx.save();
    ctx.globalAlpha = 0.6;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = rctx.pheromones.getCell(x, y);
        if (!cell) continue;

        const screen = rctx.camera.gridToScreen(x, y);

        // Trail pheromone (green glow)
        if (cell.trail > 0.01) {
          const intensity = Math.min(1, cell.trail * 2);
          const radius = 6 * scale * intensity;

          const gradient = ctx.createRadialGradient(
            screen.x, screen.y, 0,
            screen.x, screen.y, radius
          );
          gradient.addColorStop(0, `rgba(50, 205, 50, ${intensity * 0.8})`);
          gradient.addColorStop(0.5, `rgba(50, 205, 50, ${intensity * 0.4})`);
          gradient.addColorStop(1, 'rgba(50, 205, 50, 0)');

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
          ctx.fill();
        }

        // Food odor (orange glow)
        if (cell.foodOdor > 0.01) {
          const intensity = Math.min(1, cell.foodOdor * 2);
          const radius = 7 * scale * intensity;

          const gradient = ctx.createRadialGradient(
            screen.x, screen.y, 0,
            screen.x, screen.y, radius
          );
          gradient.addColorStop(0, `rgba(255, 165, 0, ${intensity * 0.6})`);
          gradient.addColorStop(0.5, `rgba(255, 165, 0, ${intensity * 0.3})`);
          gradient.addColorStop(1, 'rgba(255, 165, 0, 0)');

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
          ctx.fill();
        }

        // Alarm pheromone (red glow)
        if (cell.alarm > 0.01) {
          const intensity = Math.min(1, cell.alarm * 2);
          const radius = 8 * scale * intensity;

          const gradient = ctx.createRadialGradient(
            screen.x, screen.y, 0,
            screen.x, screen.y, radius
          );
          gradient.addColorStop(0, `rgba(255, 50, 50, ${intensity * 0.8})`);
          gradient.addColorStop(0.5, `rgba(255, 50, 50, ${intensity * 0.4})`);
          gradient.addColorStop(1, 'rgba(255, 50, 50, 0)');

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    ctx.restore();
  }

  /**
   * Render the selected car's trip path (traveled + remaining).
   */
  renderCarTripPath(ctx: CanvasRenderingContext2D, rctx: MapFeatureRenderContext): void {
    if (rctx.state.currentTool !== ParkingtonTool.SELECT) return;
    if (!rctx.state.selectedEntity || rctx.state.selectedEntity.type !== 'car') return;

    const tripPath = rctx.cars.getCarTripPath(rctx.state.selectedEntity.id);
    if (!tripPath) return;

    const cols = rctx.grid.getCols();
    const rows = rctx.grid.getRows();

    // Draw traveled path (faded amber dashed)
    if (tripPath.traveled.length > 1) {
      ctx.save();
      ctx.strokeStyle = 'rgba(234, 179, 8, 0.4)';
      ctx.lineWidth = 3;
      ctx.setLineDash([6, 4]);
      this.drawPathSegment(ctx, rctx.camera, tripPath.traveled, cols, rows);
      ctx.restore();
    }

    // Draw remaining path (bright blue solid with glow)
    if (tripPath.remaining.length > 1) {
      ctx.save();
      // Glow layer
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.25)';
      ctx.lineWidth = 7;
      ctx.setLineDash([]);
      this.drawPathSegment(ctx, rctx.camera, tripPath.remaining, cols, rows);
      // Main line
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.7)';
      ctx.lineWidth = 3;
      this.drawPathSegment(ctx, rctx.camera, tripPath.remaining, cols, rows);
      ctx.restore();
    }

    // Draw transition dot at car's current position
    const carInfo = rctx.cars.getCarInfoById(rctx.state.selectedEntity.id);
    if (carInfo) {
      const screen = rctx.camera.gridToScreen(carInfo.gridX, carInfo.gridY);
      ctx.save();
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.9)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
  }

  /**
   * Draw a polyline path on the canvas, breaking at toroidal wrap boundaries.
   */
  private drawPathSegment(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    path: { x: number; y: number }[],
    cols: number,
    rows: number,
  ): void {
    if (path.length < 2) return;

    ctx.beginPath();
    const first = camera.gridToScreen(path[0].x, path[0].y);
    ctx.moveTo(first.x, first.y);

    for (let i = 1; i < path.length; i++) {
      const dx = Math.abs(path[i].x - path[i - 1].x);
      const dy = Math.abs(path[i].y - path[i - 1].y);

      // Detect toroidal wrap (gap > 1 cell means wrapped around)
      if (dx > 1 || dy > 1) {
        ctx.stroke();
        ctx.beginPath();
        const screen = camera.gridToScreen(path[i].x, path[i].y);
        ctx.moveTo(screen.x, screen.y);
      } else {
        const screen = camera.gridToScreen(path[i].x, path[i].y);
        ctx.lineTo(screen.x, screen.y);
      }
    }

    ctx.stroke();
  }

  /**
   * Render markers for tracked car's source and destination.
   */
  renderTrackedCarMarkers(ctx: CanvasRenderingContext2D, rctx: MapFeatureRenderContext): void {
    if (rctx.state.trackedCarId === null) return;

    const carInfo = rctx.cars.getCarInfoById(rctx.state.trackedCarId);
    if (!carInfo || carInfo.mission === 'none') return;

    const scale = rctx.camera.getScale();

    // Get family info for home location
    let homeHouseId: number | null = null;
    if (carInfo.passengerFamilyId !== null) {
      const family = rctx.houses.getFamily(carInfo.passengerFamilyId);
      if (family) {
        homeHouseId = family.homeHouseId;
      }
    }

    // Draw home marker (blue house icon)
    if (homeHouseId !== null) {
      const homeHouse = rctx.houses.getHouseById(homeHouseId);
      if (homeHouse) {
        this.renderLocationMarker(ctx, rctx.camera, homeHouse.gridX, homeHouse.gridY, '#3b82f6', 'H', scale);
      }
    }

    // Draw pickup marker (yellow) if picking up
    if (carInfo.mission === 'picking_up' && carInfo.missionPickupHouseId !== null) {
      const pickupHouse = rctx.houses.getHouseById(carInfo.missionPickupHouseId);
      if (pickupHouse) {
        this.renderLocationMarker(ctx, rctx.camera, pickupHouse.gridX, pickupHouse.gridY, '#eab308', 'P', scale);
      }
    }

    // Draw destination marker (green/red based on type)
    if (carInfo.missionDestinationHouseId !== null) {
      const destHouse = rctx.houses.getHouseById(carInfo.missionDestinationHouseId);
      if (destHouse) {
        const isCorp = (destHouse as any).occupant === 'corporation';
        const color = isCorp ? '#ef4444' : '#22c55e';
        const label = isCorp ? 'C' : 'D';
        this.renderLocationMarker(ctx, rctx.camera, destHouse.gridX, destHouse.gridY, color, label, scale);
      }
    }
  }

  /**
   * Render a location marker at a grid position.
   */
  private renderLocationMarker(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    gridX: number,
    gridY: number,
    color: string,
    label: string,
    scale: number
  ): void {
    const screen = camera.gridToScreen(gridX, gridY);

    ctx.save();

    const pulse = 0.7 + 0.3 * Math.sin(Date.now() * 0.005);
    const pinHeight = 30 * scale;
    const pinRadius = 10 * scale;

    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.ellipse(screen.x, screen.y + 2, pinRadius * 0.6, pinRadius * 0.3, 0, 0, Math.PI * 2);
    ctx.fill();

    // Pin body
    ctx.fillStyle = color;
    ctx.globalAlpha = pulse;
    ctx.beginPath();
    ctx.moveTo(screen.x, screen.y);
    ctx.bezierCurveTo(
      screen.x - pinRadius, screen.y - pinHeight * 0.5,
      screen.x - pinRadius, screen.y - pinHeight,
      screen.x, screen.y - pinHeight
    );
    ctx.bezierCurveTo(
      screen.x + pinRadius, screen.y - pinHeight,
      screen.x + pinRadius, screen.y - pinHeight * 0.5,
      screen.x, screen.y
    );
    ctx.fill();

    // Pin circle
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(screen.x, screen.y - pinHeight + pinRadius * 0.3, pinRadius * 0.5, 0, Math.PI * 2);
    ctx.fill();

    // Label
    ctx.fillStyle = color;
    ctx.font = `bold ${10 * scale}px "Segoe UI", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, screen.x, screen.y - pinHeight + pinRadius * 0.3);

    ctx.restore();
  }

  /**
   * Render all colony portals on the surface.
   *
   * A sealed entrance draws nothing at all — no glow, no rim, no rising particles,
   * and above all no hole. There is a facility's foundation on that cell and the
   * facility renderer has already drawn it; what a mayor sees is a slab, with no
   * mark anywhere on it saying what it was poured over. That is the same silence
   * the surface keeps about every death on it (ADR-028).
   */
  renderPortal(ctx: CanvasRenderingContext2D, rctx: MapFeatureRenderContext): void {
    const scale = rctx.camera.getScale();

    for (let colonyIndex = 0; colonyIndex < rctx.state.portalCells.length; colonyIndex++) {
      const portalCell = rctx.state.portalCells[colonyIndex];
      if (!portalCell) continue;
      if (rctx.state.sealedPortals[colonyIndex]) continue;

      const screen = rctx.camera.gridToScreen(portalCell.x, portalCell.y);
      const colors = COLONY_PORTAL_COLORS[colonyIndex] || COLONY_PORTAL_COLORS[0];
      const isSelected = colonyIndex === rctx.state.selectedColonyIndex;

      ctx.save();

      // Pulsing glow effect
      const pulse = 0.5 + 0.5 * Math.sin(rctx.state.portalPulse + colonyIndex * Math.PI / 3);
      const baseGlowRadius = isSelected ? 45 : 20;
      const glowRadius = baseGlowRadius * scale * (0.8 + pulse * 0.4);

      // Outer yellow glow for selected
      if (isSelected) {
        const outerGlow = ctx.createRadialGradient(
          screen.x, screen.y, 0,
          screen.x, screen.y, glowRadius * 1.3
        );
        outerGlow.addColorStop(0, `rgba(255, 215, 0, ${0.6 * pulse})`);
        outerGlow.addColorStop(0.4, `rgba(255, 215, 0, ${0.3 * pulse})`);
        outerGlow.addColorStop(1, 'rgba(255, 215, 0, 0)');
        ctx.fillStyle = outerGlow;
        ctx.beginPath();
        ctx.arc(screen.x, screen.y, glowRadius * 1.3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Inner colony-colored glow
      const gradient = ctx.createRadialGradient(
        screen.x, screen.y, 0,
        screen.x, screen.y, glowRadius
      );
      const glowAlpha = isSelected ? 0.7 : 0.4;
      gradient.addColorStop(0, `rgba(${colors.r}, ${colors.g}, ${colors.b}, ${glowAlpha * pulse})`);
      gradient.addColorStop(0.5, `rgba(${colors.r}, ${colors.g}, ${colors.b}, ${glowAlpha * 0.5 * pulse})`);
      gradient.addColorStop(1, `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0)`);

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, glowRadius, 0, Math.PI * 2);
      ctx.fill();

      // Portal hole (dark center)
      ctx.fillStyle = 'rgba(30, 15, 10, 0.8)';
      ctx.beginPath();
      ctx.ellipse(screen.x, screen.y, 8 * scale, 5 * scale, 0, 0, Math.PI * 2);
      ctx.fill();

      // Yellow inner ring for selected
      if (isSelected) {
        ctx.strokeStyle = `rgba(255, 215, 0, ${0.7 + pulse * 0.3})`;
        ctx.lineWidth = 2 * scale;
        ctx.beginPath();
        ctx.ellipse(screen.x, screen.y, 6 * scale, 3.5 * scale, 0, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Portal rim with colony color
      ctx.strokeStyle = `rgba(${colors.r + 30}, ${colors.g + 30}, ${colors.b + 30}, ${0.6 + pulse * 0.4})`;
      ctx.lineWidth = 2 * scale;
      ctx.beginPath();
      ctx.ellipse(screen.x, screen.y, 10 * scale, 6 * scale, 0, 0, Math.PI * 2);
      ctx.stroke();

      // Subtle particles rising
      const particleCount = 3;
      for (let i = 0; i < particleCount; i++) {
        const angle = (rctx.state.portalPulse * 2 + i * Math.PI * 2 / particleCount + colonyIndex) % (Math.PI * 2);
        const particleY = screen.y - (rctx.state.portalPulse * 20 + i * 10) % 30;
        const particleX = screen.x + Math.sin(angle) * 5 * scale;
        const particleAlpha = 1 - ((rctx.state.portalPulse * 20 + i * 10) % 30) / 30;

        ctx.fillStyle = `rgba(${colors.r + 50}, ${colors.g + 50}, ${colors.b + 50}, ${particleAlpha * 0.5})`;
        ctx.beginPath();
        ctx.arc(particleX, particleY, 1.5 * scale, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }
  }
}
